import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Popover } from 'antd';
import { UserResponse } from 'src/types';
import data from '@emoji-mart/data';
import EmojiIcon, { EMOJI_OPTIONS, EmojiOption } from '../EmojiIcon';
import useCommentReactions from 'src/hooks/useComments';
import CommentReactionItem from '../CommentReactionItem';
import { groupReactions, normalizeReactionId } from 'src/utils/reaction';

interface Reaction {
  uuid: string;
  reaction: string;
  creator: any;
}

interface CommentReactionsCounterProps {
  data: any;
  onAddReaction: (uuid: string, reaction: any) => void;
  onRemoveReaction: (uuid: string, reaction: any) => void;
  children?: any;
}

const getEmojiDisplayData = (emojiId: string): EmojiOption => {
  const resolvedId = normalizeReactionId(emojiId);
  const emojiMartEntry = (data as any).emojis?.[resolvedId];
  if (emojiMartEntry) {
    return { id: emojiId, native: emojiMartEntry.skins[0].native, label: emojiMartEntry.name };
  }
  const legacy = EMOJI_OPTIONS.find((e) => e.id === emojiId);
  if (legacy) return legacy;
  return { id: emojiId, native: emojiId, label: emojiId };
};

const formatReactionNames = (
  reactionCreators: { name: string }[] = [],
  emojiLabel: string
) => {
  const names = reactionCreators.map((c) => c.name);
  const totalCount = names.length;
  const maxToShow = 5;

  let namesString = '';

  const namesToShow = names.slice(0, maxToShow);
  namesString = namesToShow.join(', ');

  const remainingCount = totalCount - maxToShow;
  if (remainingCount > 0) {
    namesString += ` and +${remainingCount} other${
      remainingCount > 1 ? 's' : ''
    }`;
  }

  return (
    <div style={{ fontSize: '14px', lineHeight: '1.4', padding: 0, margin: 0 }}>
      {namesString} reacted with <b>{emojiLabel}</b>
    </div>
  );
};

const CommentReactionsCounter: FC<CommentReactionsCounterProps> = ({
  data,
  onAddReaction,
  onRemoveReaction,
  children,
}) => {
  const user: UserResponse = useSelector(
    (globalState: any) => globalState.auth.user
  );

  const { removeReact, addReact, hasUserReaction, userReactions } =
    useCommentReactions({
      initialData: data,
      onReactAdd: onAddReaction,
      onReactRemove: onRemoveReaction,
    });

  const [pulsingEmojis, setPulsingEmojis] = useState<{
    up: Record<string, boolean>;
    down: Record<string, boolean>;
  }>({ up: {}, down: {} });

  const prevCountsRef = useRef<Record<string, number>>({});
  const isInitialMount = useRef(true);

  const { counts, userReactionUuids, creators } = useMemo(() => {
    const reactionsFromOthers = data.reactions.filter((reaction: Reaction) => reaction?.creator?.uuid !== user?.uuid);
    const optimisticReactions = [...reactionsFromOthers, ...userReactions];
    return groupReactions(optimisticReactions, user?.uuid);
  }, [data.reactions, userReactions, user?.uuid]);

  const handleReactionToggle = (emojiId: string) => {
    if (hasUserReaction(emojiId)) {
      removeReact(emojiId);
    } else {
      addReact(emojiId);
    }
  };

  const emojisToShow = useMemo(() => {
    const seen = new Set<string>();
    const ordered: EmojiOption[] = [];

    data.reactions.forEach((r: any) => {
      const normalizedId = normalizeReactionId(r.reaction);
      if (!seen.has(normalizedId) && counts[normalizedId] > 0) {
        seen.add(normalizedId);
        ordered.push(getEmojiDisplayData(normalizedId));
      }
    });

    return ordered;
  }, [counts, data.reactions]);

  useEffect(() => {
    const currentCounts = counts;
    const prevCounts = prevCountsRef.current;

    if (isInitialMount.current) {
      prevCountsRef.current = currentCounts;
      isInitialMount.current = false;
      return;
    }

    const pulseUp: Record<string, boolean> = {};
    const pulseDown: Record<string, boolean> = {};

    const allEmojiIds = new Set([
      ...Object.keys(currentCounts),
      ...Object.keys(prevCounts),
    ]);

    allEmojiIds.forEach((emojiId) => {
      const curr = currentCounts[emojiId] || 0;
      const prev = prevCounts[emojiId] || 0;

      if (curr > prev) {
        pulseUp[emojiId] = true;
      } else if (curr < prev) {
        pulseDown[emojiId] = true;
      }
    });

    prevCountsRef.current = currentCounts;

    if (Object.keys(pulseUp).length > 0 || Object.keys(pulseDown).length > 0) {
      setPulsingEmojis({ up: pulseUp, down: pulseDown });

      const timer = setTimeout(() => {
        setPulsingEmojis({ up: {}, down: {} });
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [counts]);

  return (
    <div className="reaction-counter-container">
      {emojisToShow.map((emoji) => {
        const count = counts[emoji.id] || 0;
        const isUserReaction = !!userReactionUuids[emoji.id];
        const shouldShowPopover = count > 0;

        const reactionContent = (
          <CommentReactionItem
            emoji={emoji}
            count={count}
            isUserReaction={isUserReaction}
            onClick={handleReactionToggle}
            isPulsingUp={pulsingEmojis.up[emoji.id]}
            isPulsingDown={pulsingEmojis.down[emoji.id]}
          />
        );

        if (shouldShowPopover) {
          const reactionCreators = creators[emoji.id] || [];
          return (
            <Popover
              key={emoji.id}
              placement="top"
              trigger="hover"
              overlayClassName="reaction-popover-list"
              content={
                <div
                  style={{ display: 'flex', alignItems: 'start', gap: '6px' }}
                >
                  <div style={{ flex: 'none', width: '30px', height: '30px' }}>
                    <EmojiIcon symbol={emoji.native} size={24} />
                  </div>
                  <div style={{ flexGrow: 1, minWidth: '150px' }}>
                    {formatReactionNames(reactionCreators, emoji.label)}
                  </div>
                </div>
              }
            >
              <span key={`span-${emoji.id}`}>{reactionContent}</span>
            </Popover>
          );
        }

        return <span key={emoji.id}>{reactionContent}</span>;
      })}

      {children}
    </div>
  );
};

export default CommentReactionsCounter;
